import { useMemo } from 'react';

import {
  TypedUseQueryHookResult,
  skipToken,
} from '@reduxjs/toolkit/dist/query/react';

import { useFetchCornerRadiiQuery } from '../cornerRadiiService';

import { AxiosBaseQueryFunction } from '~/api/axiosBaseQuery';
import { CommonProductQueryParams } from '~/api/easy-estimator-api/commonProductQueryParams';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { shouldFetchFn } from '~/api/utils';
import { FREE_CORNER_RADIUS } from '~/core/constants';
import { CornerRadiusModel } from '~/core/models';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { selectZipcode } from '~/redux/slices/zipcode';

type useCornerRadiiReturn = Omit<
  TypedUseQueryHookResult<
    CornerRadiusModel[],
    CommonProductQueryParams,
    AxiosBaseQueryFunction
  >,
  'data' | 'isFetching' | 'isError'
> & {
  cornerRadii: CornerRadiusModel[];
  isFetchingCornerRadii: boolean;
  isErrorCornerRadii: boolean;
  defaultValue: CornerRadiusModel | undefined;
};

const emptyArray = [] satisfies CornerRadiusModel[];

export const useCornerRadii = (): useCornerRadiiReturn => {
  const zipCode = useAppSelector(selectZipcode);
  const selectedStore = useSelectedStore();

  const selectedColor = useAppSelector(selectSelectedColor);
  const shouldFetch = shouldFetchFn({
    args: [zipCode, selectedStore, selectedColor],
    allowPartialForEstimate: true,
  });

  const {
    data: cornerRadii = emptyArray,
    isFetching: isFetchingCornerRadii,
    isError: isErrorCornerRadii,
    ...rest
  } = useFetchCornerRadiiQuery(
    shouldFetch
      ? {
          params: {
            zipCode,
            storeId: selectedStore?.companyId,
            prodQteGrpID: selectedColor?.qteGrpID,
          },
        }
      : skipToken
  );

  const defaultValue = useMemo(
    () =>
      cornerRadii.find((corner) => corner.productId === FREE_CORNER_RADIUS.ID),
    [cornerRadii]
  );

  return {
    cornerRadii,
    isFetchingCornerRadii,
    isErrorCornerRadii,
    defaultValue,
    ...rest,
  };
};
